import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IMemoryFilterCriteria, Contact, ContactModel, GroupModel } from "common247";

export interface IMemoryFilterComponentProps {
  criteria: IMemoryFilterCriteria;
  onChange: (updated: IMemoryFilterCriteria) => void;
  allContacts?: (Contact | ContactModel)[];
  allGroups?: GroupModel[];
}

const MEDIA_TYPES: ("all" | "image" | "video" | "audio" | "document" | "pdf")[] = [
  "all",
  "image",
  "video",
  "audio",
  "document",
  "pdf",
];

const RATINGS = [1, 2, 3, 4, 5];

/**
 * Filter controls specific to Memory entities.
 */
export function MemoryFilter({
  criteria,
  onChange,
  allContacts = [],
  allGroups = [],
}: IMemoryFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");
  const tintColor = useThemeColor({}, "tint");

  const currentTypes = criteria.mediaTypes || ["all"];
  const currentRating = criteria.minRating || 1;
  const currentContactIds = criteria.contactIds || [];
  const currentGroupIds = criteria.groupIds || [];

  const handleMediaTypeToggle = (type: "all" | "image" | "video" | "audio" | "document" | "pdf"): void => {
    if (type === "all") {
      onChange({ ...criteria, mediaTypes: ["all"] });
      return;
    }
    let updated: ("all" | "image" | "video" | "audio" | "document" | "pdf")[];
    if (currentTypes.includes(type)) {
      updated = currentTypes.filter((t) => t !== type && t !== "all");
      if (updated.length === 0) updated = ["all"];
    } else {
      updated = [...currentTypes.filter((t) => t !== "all"), type];
    }
    onChange({ ...criteria, mediaTypes: updated });
  };

  const handleContactToggle = (contactId: string): void => {
    const updated = currentContactIds.includes(contactId)
      ? currentContactIds.filter((id) => id !== contactId)
      : [...currentContactIds, contactId];
    onChange({ ...criteria, contactIds: updated.length > 0 ? updated : undefined });
  };

  const handleGroupToggle = (groupId: string): void => {
    const updated = currentGroupIds.includes(groupId)
      ? currentGroupIds.filter((id) => id !== groupId)
      : [...currentGroupIds, groupId];
    onChange({ ...criteria, groupIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Date Range */}
      <View style={styles.row}>
        <View style={styles.col}>
          <ThemedText style={[styles.label, { color: textMuted }]}>From Date</ThemedText>
          <ThemedInput
            value={criteria.dateFrom ? String(criteria.dateFrom) : ""}
            onChangeText={(txt) => onChange({ ...criteria, dateFrom: txt.trim() || undefined })}
            placeholder="e.g. 2026, 01-Jan-2026"
            style={styles.input}
          />
        </View>
        <View style={styles.col}>
          <ThemedText style={[styles.label, { color: textMuted }]}>To Date</ThemedText>
          <ThemedInput
            value={criteria.dateTo ? String(criteria.dateTo) : ""}
            onChangeText={(txt) => onChange({ ...criteria, dateTo: txt.trim() || undefined })}
            placeholder="e.g. 2026, 31-Dec-2026"
            style={styles.input}
          />
        </View>
      </View>

      {/* Media Types */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Media Types</ThemedText>
        <View style={styles.chipsWrap}>
          {MEDIA_TYPES.map((type) => (
            <FilterChip
              key={type}
              label={type.toUpperCase()}
              selected={currentTypes.includes(type)}
              onPress={() => handleMediaTypeToggle(type)}
              compact
            />
          ))}
        </View>
      </View>

      {/* Rating & Highlights */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Minimum Rating</ThemedText>
        <View style={styles.chipsWrap}>
          {RATINGS.map((star) => (
            <FilterChip
              key={star}
              label={star === 1 ? "Any Rating" : `${star}★ & up`}
              selected={currentRating === star}
              onPress={() => onChange({ ...criteria, minRating: star })}
              compact
            />
          ))}
          <FilterChip
            label="Only with Media"
            icon="images-outline"
            selected={Boolean(criteria.onlyWithMedia)}
            onPress={() => onChange({ ...criteria, onlyWithMedia: !criteria.onlyWithMedia })}
            compact
          />
        </View>
      </View>

      {/* Associated Contacts */}
      {allContacts.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Associated Contacts</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allContacts.map((c) => (
              <FilterChip
                key={c.id}
                label={c.displayName}
                icon="person-outline"
                selected={currentContactIds.includes(c.id || "")}
                onPress={() => c.id && handleContactToggle(c.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Associated Groups */}
      {allGroups.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Associated Groups</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allGroups.map((g) => (
              <FilterChip
                key={g.id}
                label={g.name}
                icon="people-outline"
                selected={currentGroupIds.includes(g.id || "")}
                onPress={() => g.id && handleGroupToggle(g.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  row: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 8,
  },
  col: {
    flex: 1,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  input: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 13,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  horizScroll: {
    flexDirection: "row",
  },
});
